import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState, SettingsState } from 'types/state'
import { LanguagesEnum } from 'types/index'
import { User } from 'types/user'
import { DEFAULT_LANGUAGE } from 'utils/settings'
import { AuthMode, AuthModeEnum } from 'types/setting'

const initialState: SettingsState = {
  numberOfQuestionsBeforeAskTicketOpen: 2,
  mode: undefined,
  token: undefined,
  areaId: undefined,
  userDetails: undefined,
  lang: LanguagesEnum.it,
  ticketReminderVisible: false,
  tenantId: undefined,
  tenantName: undefined,
  authMode: undefined,
  disableAreaSelection: false,
  chatAttachmentsEnabled: false,
  voiceEnabled: false,
  agenticEnabled: false,
  agentInputRequirements: [],
  agentInputs: {},
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setTicketReminderVisible: (
      state: SettingsState,
      { payload }: PayloadAction<SettingsState['ticketReminderVisible']>,
    ) => {
      state.ticketReminderVisible = payload
    },
    setNumberOfQuestionsBeforeAskTicketOpen: (
      state: SettingsState,
      { payload }: PayloadAction<SettingsState['numberOfQuestionsBeforeAskTicketOpen']>,
    ) => {
      state.numberOfQuestionsBeforeAskTicketOpen = payload
    },
    setMode: (state: SettingsState, { payload }: PayloadAction<SettingsState['mode']>) => {
      state.mode = payload
    },
    setToken: (state: SettingsState, { payload }: PayloadAction<SettingsState['token']>) => {
      state.token = payload
    },
    setLang: (state: SettingsState, { payload }: PayloadAction<SettingsState['lang']>) => {
      state.lang = payload
    },
    setUserDetails: (state: SettingsState, { payload }: PayloadAction<SettingsState['userDetails']>) => {
      state.userDetails = payload
    },
    login: (state: SettingsState, { payload }: PayloadAction<{ token: string; user: User }>) => {
      state.token = payload.token
      state.userDetails = payload.user
    },
    logout: (state: SettingsState) => {
      state.token = undefined
      state.userDetails = undefined
    },
    setAreaId: (state: SettingsState, { payload }: PayloadAction<SettingsState['areaId']>) => {
      state.areaId = payload
    },
    setTenantId: (state: SettingsState, { payload }: PayloadAction<SettingsState['tenantId']>) => {
      state.tenantId = payload
    },
    setTenantName: (state: SettingsState, { payload }: PayloadAction<SettingsState['tenantName']>) => {
      state.tenantName = payload
    },
    setAuthMode: (state: SettingsState, { payload }: PayloadAction<AuthMode | undefined>) => {
      state.authMode = payload
    },
    setDisableAreaSelection: (
      state: SettingsState,
      { payload }: PayloadAction<SettingsState['disableAreaSelection']>,
    ) => {
      state.disableAreaSelection = payload
    },
    setChatAttachmentsEnabled: (
      state: SettingsState,
      { payload }: PayloadAction<SettingsState['chatAttachmentsEnabled']>,
    ) => {
      state.chatAttachmentsEnabled = payload
    },
    setVoiceEnabled: (state: SettingsState, { payload }: PayloadAction<SettingsState['voiceEnabled']>) => {
      state.voiceEnabled = payload
    },
    setAgenticConfig: (
      state: SettingsState,
      { payload }: PayloadAction<{ enabled: boolean; requirements: SettingsState['agentInputRequirements'] }>,
    ) => {
      state.agenticEnabled = payload.enabled
      state.agentInputRequirements = payload.requirements
    },
    setAgentInputs: (state: SettingsState, { payload }: PayloadAction<Record<string, unknown>>) => {
      state.agentInputs = payload
    },
  },
})

export const {
  setNumberOfQuestionsBeforeAskTicketOpen,
  setMode,
  setLang,
  setToken,
  setUserDetails,
  login,
  logout,
  setTicketReminderVisible,
  setAreaId,
  setTenantId,
  setTenantName,
  setAuthMode,
  setDisableAreaSelection,
  setChatAttachmentsEnabled,
  setVoiceEnabled,
  setAgenticConfig,
  setAgentInputs,
} = settingsSlice.actions

export const settingsActions = {
  setNumberOfQuestionsBeforeAskTicketOpen,
  setMode,
  setLang,
  setToken,
  setAreaId,
  setUserDetails,
  login,
  logout,
  setTicketReminderVisible,
  setTenantId,
  setTenantName,
  setAuthMode,
  setDisableAreaSelection,
  setChatAttachmentsEnabled,
  setVoiceEnabled,
  setAgenticConfig,
  setAgentInputs,
}

export const settingsSelectors = {
  numberOfQuestionsBeforeAskTicketOpen: (state: RootState) => state.settingsState.numberOfQuestionsBeforeAskTicketOpen,
  userDetails: (state: RootState) => state.settingsState.userDetails,
  token: (state: RootState) => state.settingsState.token,
  lang: (state: RootState) => state.settingsState.lang || DEFAULT_LANGUAGE,
  isLogged: (state: RootState) => !!state.settingsState.token,
  userId: (state: RootState) => state.settingsState.userDetails?._id,
  userEmail: (state: RootState) => state.settingsState.userDetails?.email,
  ticketReminderVisible: (state: RootState) => state.settingsState.ticketReminderVisible,
  areaId: (state: RootState) => state.settingsState.areaId,
  tenantId: (state: RootState) => state.settingsState.tenantId,
  tenantName: (state: RootState) => state.settingsState.tenantName,
  authMode: (state: RootState): AuthMode => state.settingsState.authMode || AuthModeEnum.Full,
  chatAttachmentsEnabled: (state: RootState) => !!state.settingsState.chatAttachmentsEnabled,
  voiceEnabled: (state: RootState) => !!state.settingsState.voiceEnabled,
  agenticEnabled: (state: RootState) => !!state.settingsState.agenticEnabled,
  agentInputRequirements: (state: RootState) => state.settingsState.agentInputRequirements ?? [],
  agentInputs: (state: RootState): Record<string, unknown> => state.settingsState.agentInputs ?? {},
  isMultiAreaFlow: (state: RootState) => !!state.settingsState.disableAreaSelection,
}
export default settingsSlice.reducer
